import { cn, Icon, Text } from '@mntn/ui-components'
import { ProgressBar } from './progress-bar'
import { formatMoney } from './utils'

type SpendMeterBaseProps = {
  inverse?: boolean
}

type SpendMeterErrorProps = SpendMeterBaseProps & {
  hasSpendError: true
  spendAmount?: number
  totalBudget?: number
}

type SpendMeterSuccessProps = SpendMeterBaseProps & {
  hasSpendError?: false
  spendAmount: number
  totalBudget: number
}

export type SpendMeterProps = SpendMeterErrorProps | SpendMeterSuccessProps

// biome-ignore lint/plugin: RETIRED, do not copy. Convert to a named function export when touching this.
export const SpendMeter: React.FC<SpendMeterProps> = props => {
  const labelColor = props.inverse ? 'text-primary-inverse' : 'text-primary'
  const progressContainerBgColor = props.inverse ? 'bg-sub-screen-bg-inverse' : 'bg-sub-screen-bg'

  if (props.hasSpendError) {
    return (
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1">
          <Icon name="TriangleAlert" className="size-3 text-secondary shrink-0 -mt-0.5" />
          <Text variant="base-xs" className="text-secondary">
            No Spend data available
          </Text>
        </div>
        <ProgressBar
          progress={0}
          progressColorClass="bg-gradient-marketing-core"
          containerClassName={progressContainerBgColor}
        />
        <div className={cn('flex items-center justify-between gap-1', labelColor)}>
          <div className="flex items-center gap-1">
            <span className="bg-gradient-marketing-core inline-block min-w-2 min-h-2 rounded-full" />
            <Text variant="number-xs" className="text-secondary">
              --
            </Text>
          </div>
          <Text variant="number-xs" className="text-secondary">
            -- Total
          </Text>
        </div>
      </div>
    )
  }

  const { spendAmount, totalBudget } = props
  const percent = totalBudget > 0 ? Math.min((spendAmount / totalBudget) * 100, 100) : 0
  const leftToSpend = totalBudget - spendAmount

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center justify-between">
        <Text variant="base-xs" className={labelColor}>
          Spend
        </Text>
        {leftToSpend > 0 && (
          <Text variant="number-xs" className="text-secondary">
            {formatMoney(leftToSpend)} left to spend
          </Text>
        )}
      </div>
      <ProgressBar
        progress={percent}
        progressColorClass="bg-gradient-marketing-core"
        containerClassName={progressContainerBgColor}
      />
      <div className={cn('flex items-center justify-between gap-1', labelColor)}>
        <div className="flex items-center gap-1">
          <span className="bg-gradient-marketing-core inline-block min-w-2 min-h-2 rounded-full" />
          <Text variant="number-xs" className="text-secondary">
            {formatMoney(spendAmount)} Spent
          </Text>
        </div>
        <Text variant="number-xs" className="text-secondary">
          {formatMoney(totalBudget)} Total
        </Text>
      </div>
    </div>
  )
}
